/**
 * Voice Context
 *
 * Thin coordination layer for DM voice calls:
 * - Call initiation (POST /voice/calls → LiveKit token → delegate to LiveKitContext)
 * - Incoming call state management (Phase 2)
 * - Push-to-talk still handled by LiveKitContext
 *
 * All actual media is handled by LiveKitContext (LiveKit SFU).
 */

import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { apiClient } from '@/services/api.client';
import { useLiveKit } from '@/contexts/LiveKitContext';
import { useRecording } from '@/hooks/useRecording';
import socketClient from '@/services/socket.client';
import { asApiError } from '@/utils/error.util';

// Voice type definitions
export interface VoiceParticipant {
  id: string;
  sessionId: string;
  userId: string;
  joinedAt: Date;
  leftAt?: Date;
  isMuted: boolean;
  isDeafened: boolean;
  isSpeaking: boolean;
  user: {
    id: string;
    email: string;
    displayName: string;
    avatarUrl?: string;
  };
}

export interface VoiceSession {
  id: string;
  channelId?: string;
  dmThreadId?: string;
  roomId: string;
  sessionType: 'channel' | 'direct' | 'group';
  startedAt: Date;
  endedAt?: Date;
  participants: VoiceParticipant[];
}

export interface IncomingCall {
  sessionId: string;
  dmThreadId: string;
  callerId: string;
  callerName: string;
  callerAvatar?: string;
  isGroup?: boolean;
}

export interface VoiceState {
  activeSession: VoiceSession | null;
  incomingCall: IncomingCall | null;
  isConnecting: boolean;
  error: string | null;
  // Proxied from LiveKit for backward compatibility
  isMuted: boolean;
  isDeafened: boolean;
  isScreenSharing: boolean;
  isRecording: boolean;
  recordingDuration: number;
}

interface VoiceContextValue {
  voiceState: VoiceState;
  startCall: (dmThreadId: string) => Promise<void>;
  acceptCall: (sessionId: string) => Promise<void>;
  declineCall: (sessionId: string) => void;
  leaveSession: () => Promise<void>;
  toggleMute: () => void;
  toggleDeafen: () => void;
  toggleScreenShare: () => Promise<void>;
  // Recording operations
  startRecording: (options?: { title?: string; includeVideo?: boolean }) => Promise<void>;
  stopRecording: () => Promise<void>;
  formatRecordingDuration: (seconds: number) => string;
}

const VoiceContext = createContext<VoiceContextValue | undefined>(undefined);

export const VoiceProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const liveKit = useLiveKit();
  const [sessionId, setSessionId] = useState<string | undefined>(undefined);
  const recording = useRecording(sessionId);

  const [voiceState, setVoiceState] = useState<VoiceState>({
    activeSession: null,
    incomingCall: null,
    isConnecting: false,
    error: null,
    isMuted: false,
    isDeafened: false,
    isScreenSharing: false,
    isRecording: false,
    recordingDuration: 0,
  });

  /**
   * Start a new DM voice call via LiveKit
   */
  const startCall = useCallback(
    async (dmThreadId: string) => {
      try {
        setVoiceState((prev) => ({ ...prev, isConnecting: true, error: null }));

        // Create voice session via API — backend creates LiveKit room and returns token
        const response = await apiClient.post<{
          session: VoiceSession;
          token: string;
          liveKitUrl: string;
        }>('/voice/calls', { dmThreadId });

        const { session, token, liveKitUrl } = response.data;

        // Delegate to LiveKit context for actual media
        await liveKit.joinDMCall(dmThreadId, session.id, token, liveKitUrl);

        setSessionId(session.id);
        setVoiceState((prev) => ({
          ...prev,
          activeSession: session,
          isConnecting: false,
        }));

        console.log(`✅ Started DM call via LiveKit, session: ${session.id}`);
      } catch (error) {
        console.error('Failed to start call:', error);
        setVoiceState((prev) => ({
          ...prev,
          isConnecting: false,
          error: asApiError(error).response?.data?.error || asApiError(error).message || 'Failed to start call',
        }));
      }
    },
    [liveKit]
  );

  /**
   * Leave current voice session
   */
  const leaveSession = useCallback(async () => {
    if (!voiceState.activeSession && !liveKit.state.dmThreadId) return;

    try {
      await liveKit.leaveDMCall();
      setSessionId(undefined);

      setVoiceState({
        activeSession: null,
        incomingCall: null,
        isConnecting: false,
        error: null,
        isMuted: false,
        isDeafened: false,
        isScreenSharing: false,
        isRecording: false,
        recordingDuration: 0,
      });
    } catch (error) {
      console.error('Failed to leave session:', error);
    }
  }, [voiceState.activeSession, liveKit]);

  /**
   * Proxy mute/deafen/screenshare to LiveKit
   */
  const toggleMute = useCallback(() => {
    liveKit.toggleMute();
  }, [liveKit]);

  const toggleDeafen = useCallback(() => {
    liveKit.toggleDeafen();
  }, [liveKit]);

  const toggleScreenShare = useCallback(async () => {
    await liveKit.toggleScreenShare();
  }, [liveKit]);

  /**
   * Accept an incoming call
   */
  const acceptCall = useCallback(
    async (callSessionId: string) => {
      try {
        setVoiceState((prev) => ({ ...prev, isConnecting: true, incomingCall: null, error: null }));

        // Join the existing session — backend returns LiveKit token
        const response = await apiClient.post<{
          session: VoiceSession;
          token: string;
          liveKitUrl: string;
        }>(`/voice/calls/${callSessionId}/join`);

        const { session, token, liveKitUrl } = response.data;

        // Delegate to LiveKit
        await liveKit.joinDMCall(session.dmThreadId || '', session.id, token, liveKitUrl);

        setSessionId(session.id);
        setVoiceState((prev) => ({
          ...prev,
          activeSession: session,
          isConnecting: false,
        }));

        console.log(`✅ Accepted call, joined session: ${session.id}`);
      } catch (error) {
        console.error('Failed to accept call:', error);
        setVoiceState((prev) => ({
          ...prev,
          isConnecting: false,
          incomingCall: null,
          error: asApiError(error).response?.data?.error || asApiError(error).message || 'Failed to accept call',
        }));
      }
    },
    [liveKit]
  );

  /**
   * Decline an incoming call
   */
  const declineCall = useCallback(
    (callSessionId: string) => {
      const socket = socketClient.getSocket();
      socket?.emit('voice:decline-call', { sessionId: callSessionId });
      setVoiceState((prev) => ({ ...prev, incomingCall: null }));
    },
    []
  );

  /**
   * WebSocket listeners for incoming call events
   */
  useEffect(() => {
    const socket = socketClient.getSocket();
    if (!socket) return;

    const handleIncomingCall = (data: IncomingCall) => {
      // Don't show if already in a call
      if (voiceState.activeSession || liveKit.state.isConnected) return;
      setVoiceState((prev) => ({ ...prev, incomingCall: data }));
    };

    const handleCallCancelled = () => {
      setVoiceState((prev) => ({ ...prev, incomingCall: null }));
    };

    const handleCallTimeout = () => {
      setVoiceState((prev) => ({ ...prev, incomingCall: null }));
    };

    socket.on('voice:incoming-call', handleIncomingCall);
    socket.on('voice:call-cancelled', handleCallCancelled);
    socket.on('voice:call-timeout', handleCallTimeout);

    return () => {
      socket.off('voice:incoming-call', handleIncomingCall);
      socket.off('voice:call-cancelled', handleCallCancelled);
      socket.off('voice:call-timeout', handleCallTimeout);
    };
  }, [voiceState.activeSession, liveKit.state.isConnected]);

  // Sync recording state
  useEffect(() => {
    setVoiceState(prev => ({
      ...prev,
      isRecording: recording.isRecording,
      recordingDuration: recording.recordingDuration,
    }));
  }, [recording.isRecording, recording.recordingDuration]);

  // Build composite state: own state + LiveKit state for DM calls
  const compositeState: VoiceState = {
    ...voiceState,
    // When LiveKit has an active DM call, sync connection state
    isMuted: liveKit.state.dmThreadId ? liveKit.state.isMuted : voiceState.isMuted,
    isDeafened: liveKit.state.dmThreadId ? liveKit.state.isDeafened : voiceState.isDeafened,
    isScreenSharing: liveKit.state.dmThreadId ? liveKit.state.isScreenSharing : voiceState.isScreenSharing,
    // If LiveKit has a DM session but we lost track, reconstruct
    activeSession: voiceState.activeSession || (liveKit.state.dmThreadId ? {
      id: liveKit.state.sessionId || '',
      dmThreadId: liveKit.state.dmThreadId,
      roomId: '',
      sessionType: 'direct' as const,
      startedAt: new Date(),
      participants: [],
    } : null),
  };

  const value: VoiceContextValue = {
    voiceState: compositeState,
    startCall,
    acceptCall,
    declineCall,
    leaveSession,
    toggleMute,
    toggleDeafen,
    toggleScreenShare,
    startRecording: recording.startRecording,
    stopRecording: recording.stopRecording,
    formatRecordingDuration: recording.formatDuration,
  };

  return <VoiceContext.Provider value={value}>{children}</VoiceContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components -- context hook co-located with its Provider (fast-refresh only)
export const useVoice = () => {
  const context = useContext(VoiceContext);
  if (!context) {
    throw new Error('useVoice must be used within VoiceProvider');
  }
  return context;
};
